import React, { useEffect, useRef } from 'react';
import { todayIso } from '../utils/dateUtils';
import useIsMobile from '../hooks/useIsMobile';

// Fallback domestic ID list — used only if the ID Type Master (REQ-09,
// GET /id-types) hasn't loaded yet or a parent screen doesn't pass it.
const ID_TYPES_FALLBACK = ['Aadhaar', 'Driving Licence', 'Voter ID', 'PAN Card', 'Passport'];
const GENDERS = ['Male', 'Female', 'Other'];
const VISIT_PURPOSES = ['Business', 'Tourism', 'Medical', 'Education', 'Transit', 'Official', 'Other'];
const CFORM_STATUSES = ['PENDING', 'SUBMITTED', 'EXEMPTED'];
const normalizeCountry = (value) => String(value || '').trim().toLowerCase();

/**
 * Reusable compliance step component used in Walk-In Check-In and Reservation.
 *
 * Props:
 *   values        — { guest_name, gender, dob, nationality, address, id_proof_type, id_proof_no,
 *                    purpose_of_visit, is_foreign_guest, passport_number, visa_number,
 *                    visa_expiry_date, arrival_stamp_date, date_of_arrival_in_india,
 *                    place_of_issue, cform_submission_status,
 *                    id_scan_data, id_scan_name, passport_scan_data, passport_scan_name }
 *   onChange(name, value) — setter
 *   fieldRules    — array from GET /compliance/fields (controls mandatory/visible per field)
 *   errors        — { fieldName: errorMsg }
 *   policyConfig  — { require_id_document_upload, require_passport_upload, ... }
 *   showGuestName — if true, renders a "Guest Name" text field at top (for additional guests)
 *   idTypeOptions — array from GET /id-types (REQ-09); { code, label, applies_to, is_active }.
 *                   Falls back to ID_TYPES_FALLBACK if empty.
 */
function PmsComplianceCheckin({
  values = {},
  onChange,
  fieldRules = [],
  errors = {},
  policyConfig = {},
  countryOptions = [],
  idTypeOptions = [],
  hotelCountry = 'India',
  showGuestName = false
}) {
  const { isMobile } = useIsMobile();
  const v = values;
  const domesticIdTypes = idTypeOptions.length
    ? idTypeOptions
        .filter((t) => t.is_active !== false && ['DOMESTIC', 'ALL'].includes(String(t.applies_to || 'ALL').toUpperCase()))
        .map((t) => t.label)
    : ID_TYPES_FALLBACK;

  // Build a quick lookup: field_name → rule
  const ruleMap = {};
  fieldRules.forEach((r) => { ruleMap[r.field_name] = r; });

  const nationalityText = String(v.nationality || '').trim();
  const normalizedHotelCountry = normalizeCountry(hotelCountry);
  const normalizedNationality = normalizeCountry(nationalityText);
  const defaultNationalityToHotelCountry = Boolean(policyConfig?.default_nationality_to_hotel_country);
  const captureIdExpiry = Boolean(policyConfig?.capture_id_expiry_if_applicable);
  const requireIdDocUpload = Boolean(policyConfig?.require_id_document_upload);
  const requirePassportUpload = Boolean(policyConfig?.require_passport_upload);

  const isForeign = Boolean(v.is_foreign_guest) || (
    normalizedNationality
      ? (normalizedHotelCountry ? normalizedNationality !== normalizedHotelCountry : (normalizedNationality !== 'india' && normalizedNationality !== 'indian'))
      : false
  );

  useEffect(() => {
    if (!defaultNationalityToHotelCountry) return;
    if (nationalityText) return;
    if (!hotelCountry) return;
    onChange('nationality', hotelCountry);
    onChange('is_foreign_guest', false);
  }, [defaultNationalityToHotelCountry, nationalityText, hotelCountry, onChange]);

  const isVisible = (fieldName) => {
    const r = ruleMap[fieldName];
    if (!r) return true; // show by default if rule not loaded yet
    if (!r.is_visible) return false;
    if (r.applies_to === 'FOREIGN' && !isForeign) return false;
    if (r.applies_to === 'INDIAN' && isForeign) return false;
    return true;
  };

  const isMandatory = (fieldName) => {
    const r = ruleMap[fieldName];
    if (!r) return false;
    return r.is_mandatory;
  };

  const labelFor = (fieldName, fallback) => {
    return ruleMap[fieldName]?.label || fallback;
  };

  const inputStyle = (fieldName) => ({
    width: '100%',
    padding: '7px 10px',
    border: errors[fieldName] ? '1.5px solid var(--theme-danger)' : '1px solid #d1d5db',
    borderRadius: 6,
    fontSize: 13,
    boxSizing: 'border-box',
    background: '#fff',
  });

  const labelStyle = { fontSize: 12, fontWeight: 600, color: 'var(--theme-text)', marginBottom: 3, display: 'block' };
  const fieldWrap = { marginBottom: 14 };
  const errorStyle = { color: 'var(--theme-danger)', fontSize: 11, marginTop: 2 };

  const field = (name, label, children) => {
    if (!isVisible(name)) return null;
    return (
      <div style={fieldWrap} key={name}>
        <label style={labelStyle}>
          {labelFor(name, label)}
          {isMandatory(name) && <span style={{ color: 'var(--theme-danger)', marginLeft: 3 }}>*</span>}
        </label>
        {children}
        {errors[name] && <div style={errorStyle}>{errors[name]}</div>}
      </div>
    );
  };

  const sectionTitle = (title) => (
    <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase',
      letterSpacing: '0.08em', marginBottom: 10, marginTop: 4, borderBottom: '1px solid #e5e7eb',
      paddingBottom: 4 }}>
      {title}
    </div>
  );

  // ── Domestic ID scan upload — multi-file (front+back of a card, multiple
  // passport pages, etc). id_scan_data/id_scan_name are arrays; a legacy
  // reservation saved before this feature existed may still hand this
  // component a bare string, so every read of them below normalizes first.
  const MAX_ID_SCANS = 5;
  const asList = (value) => (Array.isArray(value) ? value.filter(Boolean) : (value ? [value] : []));
  const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (ev) => resolve(ev.target.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const idScanRef = useRef(null);
  const handleIdScan = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ''; // allow re-picking the same file(s) again later
    if (!files.length) return;
    const existingData = asList(v.id_scan_data);
    const existingNames = asList(v.id_scan_name);
    const room = Math.max(0, MAX_ID_SCANS - existingData.length);
    if (room <= 0) { alert(`You can upload up to ${MAX_ID_SCANS} images for this document.`); return; }
    const oversized = files.filter((f) => f.size > 5 * 1024 * 1024);
    const valid = files.filter((f) => f.size <= 5 * 1024 * 1024).slice(0, room);
    if (oversized.length) alert(`${oversized.length} file(s) were over 5 MB and were skipped.`);
    if (files.length - oversized.length > room) alert(`Only ${room} more image(s) can be added (max ${MAX_ID_SCANS} total).`);
    if (!valid.length) return;
    Promise.all(valid.map(readFileAsDataUrl)).then((dataUrls) => {
      onChange('id_scan_data', [...existingData, ...dataUrls]);
      onChange('id_scan_name', [...existingNames, ...valid.map((f) => f.name)]);
    });
  };
  const removeIdScanAt = (idx) => {
    const nextData = asList(v.id_scan_data).filter((_, i) => i !== idx);
    const nextNames = asList(v.id_scan_name).filter((_, i) => i !== idx);
    // An empty array is truthy in JS — mandatory-field checks elsewhere
    // (`!guestData.id_scan_data`) rely on '' for "nothing uploaded", so keep
    // emitting that same falsy value once the last image is removed instead
    // of leaving a `[]` that would silently read as "still filled in".
    onChange('id_scan_data', nextData.length ? nextData : '');
    onChange('id_scan_name', nextNames.length ? nextNames : '');
  };

  // ── Passport scan upload ─────────────────────────────────────
  const passportScanRef = useRef(null);
  const handlePassportScan = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert('File size must be under 5 MB'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => {
      onChange('passport_scan_data', ev.target.result);
      onChange('passport_scan_name', file.name);
    };
    reader.readAsDataURL(file);
  };

  const scanUploadBlock = ({
    label,
    mandatory,
    scanDataKey,
    scanNameKey,
    inputRef,
    onFileChange,
    uploadBtnColor = 'var(--theme-primary)',
    errorKey,
    multi = false,
    onRemoveAt,
  }) => {
    const items = multi ? asList(v[scanDataKey]) : (v[scanDataKey] ? [v[scanDataKey]] : []);
    const names = multi ? asList(v[scanNameKey]) : (v[scanNameKey] ? [v[scanNameKey]] : []);
    const hasAny = items.length > 0;
    const kindOf = (data) => {
      if (data.startsWith('data:image')) return 'image';
      if (data.startsWith('data:application/pdf')) return 'pdf';
      return 'saved';
    };
    return (
      <div style={{ ...fieldWrap, border: mandatory && errors[errorKey] ? '1.5px solid var(--theme-danger)' : '1px solid #e5e7eb',
        borderRadius: 8, padding: '10px 14px', background: '#f8fbff' }}>
        <label style={{ ...labelStyle, marginBottom: 6 }}>
          {label}
          {mandatory && <span style={{ color: 'var(--theme-danger)', marginLeft: 3 }}>*</span>}
        </label>
        <input ref={inputRef} type='file' accept='image/*,.pdf' multiple={multi}
          style={{ display: 'none' }} onChange={onFileChange} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button type='button' onClick={() => inputRef.current?.click()}
            style={{ padding: '6px 14px', background: uploadBtnColor, color: '#fff', border: 'none',
              borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            {hasAny ? (multi ? '+ Add More' : '↻ Replace') : '↑ Upload'}
          </button>
          {multi && hasAny && (
            <span style={{ fontSize: 11, color: '#6b7280' }}>{items.length} of {MAX_ID_SCANS} uploaded</span>
          )}
          {!multi && names[0] && (
            <span style={{ fontSize: 12, color: 'var(--theme-text)' }}>{names[0]}</span>
          )}
          {!multi && hasAny && (
            <button type='button'
              onClick={() => { onChange(scanDataKey, ''); onChange(scanNameKey, ''); }}
              style={{ fontSize: 11, color: 'var(--theme-danger)', background: 'none', border: 'none', cursor: 'pointer' }}>
              ✕ Remove
            </button>
          )}
        </div>

        {multi ? (
          hasAny && (
            <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {items.map((data, idx) => {
                const kind = kindOf(data);
                const name = names[idx] || `Document ${idx + 1}`;
                return (
                  <div key={idx} style={{ position: 'relative', width: 100 }}>
                    <button type='button' onClick={() => (onRemoveAt ? onRemoveAt(idx) : null)}
                      title='Remove this image'
                      style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%',
                        border: '1px solid #e5e7eb', background: '#fff', color: 'var(--theme-danger)', cursor: 'pointer',
                        fontSize: 12, lineHeight: '18px', padding: 0, boxShadow: '0 1px 2px rgba(0,0,0,0.15)' }}>
                      ✕
                    </button>
                    {kind === 'image' ? (
                      <img src={data} alt={name}
                        style={{ width: 100, height: 80, borderRadius: 6, border: '1px solid #e5e7eb', objectFit: 'cover', display: 'block' }} />
                    ) : (
                      <div style={{ width: 100, height: 80, borderRadius: 6, border: '1px solid #e5e7eb',
                        background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 22, color: kind === 'pdf' ? '#2563eb' : '#16a34a' }}>
                        {kind === 'pdf' ? '📄' : '✓'}
                      </div>
                    )}
                    <div style={{ fontSize: 10, color: '#6b7280', marginTop: 3, whiteSpace: 'nowrap',
                      overflow: 'hidden', textOverflow: 'ellipsis' }} title={name}>
                      {name}
                    </div>
                  </div>
                );
              })}
            </div>
          )
        ) : (
          <>
            {items[0] && kindOf(items[0]) === 'image' && (
              <img src={items[0]} alt={label}
                style={{ marginTop: 8, maxWidth: 300, maxHeight: 180, borderRadius: 6,
                  border: '1px solid #e5e7eb', objectFit: 'contain', display: 'block' }} />
            )}
            {items[0] && kindOf(items[0]) === 'pdf' && (
              <div style={{ marginTop: 6, fontSize: 12, color: '#2563eb' }}>
                PDF uploaded: {names[0]}
              </div>
            )}
            {items[0] && kindOf(items[0]) === 'saved' && (
              <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 12, color: '#16a34a', fontWeight: 600 }}>✓ Existing scan on file</span>
                <a href={items[0]} target='_blank' rel='noreferrer'
                  style={{ fontSize: 12, color: '#2563eb', textDecoration: 'underline' }}>
                  View
                </a>
              </div>
            )}
          </>
        )}

        {mandatory && errors[errorKey] && (
          <div style={errorStyle}>{errors[errorKey]}</div>
        )}
      </div>
    );
  };

  return (
    <div style={{ fontFamily: 'inherit' }}>
      {showGuestName && (
        <>
          <div style={fieldWrap}>
            <label style={labelStyle}>
              Guest Name <span style={{ color: 'var(--theme-danger)', marginLeft: 3 }}>*</span>
            </label>
            <input
              type='text'
              value={v.guest_name || ''}
              placeholder='Full name of this guest'
              onChange={(e) => onChange('guest_name', e.target.value)}
              style={inputStyle('guest_name')}
            />
            {errors['guest_name'] && <div style={errorStyle}>{errors['guest_name']}</div>}
          </div>
          {/* Optional — without a mobile/email this guest can't be matched to
              their own Guest Master profile on a repeat visit, so each stay
              creates a fresh, un-linked profile instead of building history. */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            <div style={fieldWrap}>
              <label style={labelStyle}>Mobile Number <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span></label>
              <input
                type='tel'
                value={v.mobile_number || ''}
                placeholder='10-digit mobile number'
                onChange={(e) => onChange('mobile_number', e.target.value.replace(/[^0-9]/g, '').slice(0, 15))}
                style={inputStyle('mobile_number')}
              />
            </div>
            <div style={fieldWrap}>
              <label style={labelStyle}>Email <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span></label>
              <input
                type='email'
                value={v.email || ''}
                placeholder='guest@example.com'
                onChange={(e) => onChange('email', e.target.value)}
                style={inputStyle('email')}
              />
            </div>
          </div>
        </>
      )}
      {sectionTitle('Identity')}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : '1fr 1fr 1fr', gap: '0 16px' }}>
        {field('gender', 'Gender',
          <select value={v.gender || ''} onChange={(e) => onChange('gender', e.target.value)}
            style={inputStyle('gender')}>
            <option value=''>Select Gender</option>
            {GENDERS.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
        )}

        {/* DOB field */}
        {isVisible('dob') && (
          <div style={fieldWrap}>
            <label style={labelStyle}>
              {labelFor('dob', 'Date of Birth (DD/MM/YYYY)')}
              {isMandatory('dob') && <span style={{ color: 'var(--theme-danger)', marginLeft: 3 }}>*</span>}
            </label>
            <input type='date' lang='en-GB' value={v.dob || ''} onChange={(e) => onChange('dob', e.target.value)}
              style={inputStyle('dob')} max={todayIso()} />
            {errors['dob'] && <div style={errorStyle}>{errors['dob']}</div>}
          </div>
        )}

        {field('nationality', 'Nationality',
          countryOptions.length ? (
            <select value={v.nationality || ''} onChange={(e) => {
              const nat = e.target.value;
              onChange('nationality', nat);
              const normalized = normalizeCountry(nat);
              const isForeignNat = normalized
                ? (normalizedHotelCountry ? normalized !== normalizedHotelCountry : (normalized !== 'india' && normalized !== 'indian'))
                : false;
              onChange('is_foreign_guest', isForeignNat);
            }} style={inputStyle('nationality')}>
              <option value=''>Select Nationality</option>
              {countryOptions.map((c) => {
                const name = String(c.country_name || '').trim();
                return <option key={c.id || name} value={name}>{name}</option>;
              })}
              {/* Bug 8664 — preserve a prefilled nationality not present in the
                  country master so it displays instead of resetting to blank. */}
              {v.nationality && !countryOptions.some((c) => String(c.country_name || '').trim() === String(v.nationality).trim()) && (
                <option value={v.nationality}>{v.nationality}</option>
              )}
            </select>
          ) : (
            <input type='text' value={v.nationality || ''} placeholder='e.g. Indian'
              onChange={(e) => {
                const nat = e.target.value;
                onChange('nationality', nat);
                const normalized = normalizeCountry(nat);
                const isForeignNat = normalized
                  ? (normalizedHotelCountry ? normalized !== normalizedHotelCountry : (normalized !== 'india' && normalized !== 'indian'))
                  : false;
                onChange('is_foreign_guest', isForeignNat);
              }}
              style={inputStyle('nationality')} />
          )
        )}
      </div>

      {!isForeign && (
        <>
          {sectionTitle('ID Verification')}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            {field('id_proof_type', 'ID Type',
              <select value={v.id_proof_type || ''} onChange={(e) => onChange('id_proof_type', e.target.value)}
                style={inputStyle('id_proof_type')}>
                <option value=''>Select ID Type</option>
                {domesticIdTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                {/* Bug 8664 — preserve a prefilled value (e.g. from Guest Master:
                    'Passport', 'Other', 'Driving License') that isn't in the
                    standard list, so it displays instead of showing blank. */}
                {v.id_proof_type && !domesticIdTypes.includes(v.id_proof_type) && (
                  <option value={v.id_proof_type}>{v.id_proof_type}</option>
                )}
              </select>
            )}
            {field('id_proof_no', 'ID Number',
              <input type='text' value={v.id_proof_no || ''} placeholder='ID document number'
                onChange={(e) => onChange('id_proof_no', e.target.value.toUpperCase().replace(/[^A-Z0-9\-]/g, ''))}
                style={inputStyle('id_proof_no')} />
            )}
            {captureIdExpiry && field('id_expiry_date', 'ID Expiry Date (DD/MM/YYYY)',
              <input type='date' lang='en-GB' value={v.id_expiry_date || ''}
                min={todayIso()}
                onChange={(e) => onChange('id_expiry_date', e.target.value)}
                style={inputStyle('id_expiry_date')} />
            )}
          </div>

          {/* Domestic ID scan upload — multi-file (e.g. front + back) */}
          {scanUploadBlock({
            label: 'ID Proof Document (Photo / Scan)',
            mandatory: requireIdDocUpload,
            scanDataKey: 'id_scan_data',
            scanNameKey: 'id_scan_name',
            inputRef: idScanRef,
            onFileChange: handleIdScan,
            uploadBtnColor: '#047857',
            errorKey: 'id_scan_data',
            multi: true,
            onRemoveAt: removeIdScanAt,
          })}
        </>
      )}

      {field('address', 'Permanent Address',
        <textarea value={v.address || ''} placeholder='Full permanent address'
          onChange={(e) => onChange('address', e.target.value)} rows={2}
          style={{ ...inputStyle('address'), resize: 'vertical' }} />
      )}

      {sectionTitle('Visit Details')}
      {field('purpose_of_visit', 'Purpose of Visit',
        <select value={v.purpose_of_visit || ''} onChange={(e) => onChange('purpose_of_visit', e.target.value)}
          style={inputStyle('purpose_of_visit')}>
          <option value=''>Select Purpose</option>
          {VISIT_PURPOSES.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
      )}

      {/* Foreign Guest Section */}
      {isForeign && (
        <>
          {sectionTitle('Foreign Guest Details')}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            {field('passport_number', 'Passport Number',
              <input type='text' value={v.passport_number || ''} placeholder='Passport number'
                onChange={(e) => onChange('passport_number', e.target.value)}
                style={inputStyle('passport_number')} />
            )}
            {field('visa_number', 'Visa Number',
              <input type='text' value={v.visa_number || ''} placeholder='Visa number'
                onChange={(e) => onChange('visa_number', e.target.value)}
                style={inputStyle('visa_number')} />
            )}
            {field('visa_expiry_date', 'Visa Expiry Date (DD/MM/YYYY)',
              <input type='date' lang='en-GB' value={v.visa_expiry_date || ''}
                min={todayIso()}
                onChange={(e) => onChange('visa_expiry_date', e.target.value)}
                style={inputStyle('visa_expiry_date')} />
            )}
            {field('arrival_stamp_date', 'Arrival Stamp Date',
              <input type='date' value={v.arrival_stamp_date || ''}
                max={todayIso()}
                onChange={(e) => onChange('arrival_stamp_date', e.target.value)}
                style={inputStyle('arrival_stamp_date')} />
            )}
            {field('date_of_arrival_in_india', 'Date of Arrival in India',
              <input type='date' value={v.date_of_arrival_in_india || ''}
                max={todayIso()}
                onChange={(e) => onChange('date_of_arrival_in_india', e.target.value)}
                style={inputStyle('date_of_arrival_in_india')} />
            )}
            {field('place_of_issue', 'Place of Issue (Passport)',
              <input type='text' value={v.place_of_issue || ''} placeholder='City where passport was issued'
                onChange={(e) => onChange('place_of_issue', e.target.value)}
                style={inputStyle('place_of_issue')} />
            )}
          </div>
          {field('cform_submission_status', 'C-Form Status',
            <select value={v.cform_submission_status || 'PENDING'}
              onChange={(e) => onChange('cform_submission_status', e.target.value)}
              style={inputStyle('cform_submission_status')}>
              {CFORM_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          )}

          {/* Passport scan upload */}
          {scanUploadBlock({
            label: 'Passport Copy (Photo Page)',
            mandatory: requirePassportUpload,
            scanDataKey: 'passport_scan_data',
            scanNameKey: 'passport_scan_name',
            inputRef: passportScanRef,
            onFileChange: handlePassportScan,
            uploadBtnColor: 'var(--theme-primary)',
            errorKey: 'passport_scan_data',
          })}
        </>
      )}
    </div>
  );
}

export default PmsComplianceCheckin;
